"use client"
import BackgroundHero from "~/app/_components/section/hero/backgroundHero"
import heartTaglineImg from "public/images/hearts/half-heart-with-tag-line.png"
import heartImg from "public/images/hearts/Half-heart-element.png"
import heroImg from "public/images/about-us/sustainability/Main-Image.png"
import australiaShapedImg from "public/images/about-us/sustainability/Australia-shaped-picture.png"
import SimpleTextSection from "~/app/_components/section/simpleText"
import { CommunityTextSection, NetZeroTextSection } from "~/app/_components/section/text/simpleTextSections"
import SOCIAL_ENTERPRISES from "~/content/feature/socialEnterprises"
import { type FeatureCardProps } from "~/app/_components/section/feature/featureCard"
import EwasteSection from "~/app/_components/pageSpecific/about/ewasteSection"
import TextLink from "~/app/_components/common/textLink"
import { ArrowLongRightIcon } from "@heroicons/react/20/solid"
import { getBorderClass } from "~/styles/styles"
import CharitiesAnimateSection from "~/app/_components/pageSpecific/about/charitiesSection"
import FeatureGrid from "~/app/_components/section/feature/featureGrid"
import SplitTextSection from "~/app/_components/section/splitText"
import GuideDogSection from "~/app/_components/pageSpecific/about/guideDogsSection"
import { useEffect, useRef } from "react"
import { useSearchParams } from "next/navigation"
import scrollToSection from "~/app/_components/common/scrollToSection"
import { CHARITIES_SECTION_CONSTANTS } from "~/app/about-us/community-and-sustainability/CHARITIES_SECTION_CONSTANTS"
import FeatureSectionWithImage from "~/app/_components/section/feature/withImage"
import Image from "next/image"
import nvironmentImage from "public/images/about-us/sustainability/Protecting-Our-Planet-Environmental-Sustainability.png"
import { ContactButton } from "~/app/_components/pageSpecific/contact/contactButton"
import OurSupplyChain from "~/app/_components/pageSpecific/about/ourSupplyChain"

const CommunitySustainabilityPageLayout = () => {
  const charitiesRef = useRef<HTMLDivElement | null>(null)

  const searchParams = useSearchParams()
  const params = searchParams.get("state")
  const state: string | undefined = params as string | undefined

  useEffect(() => {
    const refs = [{ title: CHARITIES_SECTION_CONSTANTS.CHARITY, ref: charitiesRef }]
    if (state) {
      scrollToSection(state, refs)
    }
  }, [state])

  const imageHeight = `h-[15rem] md:h-[15rem] overflow-hidden`
  const cardClass = `max-w-[20rem] lg:max-w-[16rem] mx-auto`
  const imageClass = `object-top`
  const imageOnHover = "scale"
  const titleClass = "text-base"
  const WE_HELP_OTHERS: FeatureCardProps[] = [
    {
      cardClass,
      imageHeight,
      imageOnHover,
      imageClass,
      captionBg: "bg-purpleblue group-hover:bg-purpleblue/90",
      image: "/images/about-us/sustainability/Blood-Donor.png",
      title: "Our Red Cross Lifeblood team",
      titleClass,
      body: "Many Incluso team volunteer to donate blood to save lives through the Red Cross.",
    },
    {
      cardClass,
      imageHeight,
      imageOnHover,
      imageClass,
      captionBg: "bg-purpleblue group-hover:bg-purpleblue/90",
      image: "/images/about-us/sustainability/School.png",
      title: "Supporting local school communities",
      titleClass,
      body: `We donate prizes, giveaways and make financial contributions towards school and for-purpose organisation fundraising activities.`,
    },
    {
      cardClass,
      imageHeight,
      imageOnHover,
      imageClass,
      captionBg: "bg-purpleblue group-hover:bg-purpleblue/90",
      image: "/images/about-us/sustainability/Scholarship.png",
      title: "Scholarship programs",
      titleClass,
      body: "We recognise academic excellence and  social hardship through our scholarship programs.",
    },
  ]

  return (
    <>
      <BackgroundHero
        smallTitle="Community and sustainability"
        title={
          <h1 className="leading-9 xs:text-4xl ml:w-[40rem] ml:text-[2.6rem] lg:mt-3 xl:w-[48rem] xl:text-5xl">
            Creating the world <span className="block text-green-light">that we want</span>
          </h1>
        }
        image={heartTaglineImg}
        imgRight
        bgImage={heroImg}
        imageOpacity="opacity-40 lg:opacity-100"
        bgImagePosition="right"
        flexCenter
      />
      <FeatureGrid
        theme="dark-purpleblue"
        title="Our approach to community"
        titleColour="text-green-light"
        body={
          <>
            <p className="">
              Since our founding in Melbourne, we have proudly supported the communities in which we operate. From providing academic scholarships Australia
              wide to schools in recognition of educational excellence, through to local procurement Australia wide of goods and services from social
              enterprises, employee volunteering programs and championing reconciliation, we are proud to take a stance and give back.
            </p>
            <p className="mt-10 mb-6 font-bold text-resp-2xl">
              <span className="text-white">Our community </span>
              <span className="text-green"> strategy </span>
              <span className="text-white">focuses on:</span>
            </p>
          </>
        }
        textMaxWOverride="max-w-3xl"
        itemVariant="3xl-stacked-centre"
        items={[
          {
            itemClass: "bg-gray-950",
            hoverClass: "hover:bg-white/5",
            icon: "/icons/Sustainability Icons/community/local-procurement-Australia-Wide-green.svg",
            title: `Local procurement Australia wide`,
          },
          {
            itemClass: "bg-gray-950",
            hoverClass: "hover:bg-white/5",
            icon: "/icons/Sustainability Icons/community/Local Talent-green.svg",
            title: `Hiring local talent`,
          },
          {
            itemClass: "bg-gray-950",
            hoverClass: "hover:bg-white/5",
            icon: "/icons/Sustainability Icons/community/Support less fortunate-green.svg",
            title: `Supporting those less fortunate`,
          },
          {
            itemClass: "bg-gray-950",
            hoverClass: "hover:bg-white/5",
            icon: "/icons/Sustainability Icons/community/Championing Reconciliation-green.svg",
            title: `Championing reconciliation Australia wide`,
          },
          {
            itemClass: "bg-gray-950",
            hoverClass: "hover:bg-white/5",
            icon: "/icons/Sustainability Icons/community/Support Future Generations-green.svg",
            title: `Supporting future generations`,
          },
          {
            itemClass: "bg-gray-950",
            hoverClass: "hover:bg-white/5",
            icon: "/icons/Sustainability Icons/community/Building better Australia-green.svg",
            title: `Building a better Australia`,
          },
        ]}
        afterAction={
          <div className="text-resp-2xl text-center mt-10">
            <span className="text-white">Through these key strategic pillars, </span>
            <span className="text-white block">
              we are working to <span className="text-green font-bold ">create the world that we want</span>.
            </span>
          </div>
        }
        gridCols="grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
        gridGaps="gap-x-6 gap-y-4"
        padding="md"
        customBorder="border-t-4 border-white"
      />
      <OurSupplyChain />
      <GuideDogSection />
      <SimpleTextSection
        theme="light"
        title="Supporting local"
        body="100% of our workforce is local to Australia and New Zealand, so we prioritise hiring local talent. In 2024, over 90% of our non-inventory supply chain was sourced through supporting Australian businesses."
        flexHeight
      />
      <SplitTextSection
        theme="dark-purpleblue"
        title="Recognising commitment"
        titleColour="text-green-light"
        body="For over 18 years, we have partnered with schools through academic scholarships Australia wide, in-kind gifts and fundraising activities, in support of our future leaders. "
        image={"/images/about-us/sustainability/Scholarship-support.png"}
        iconImage={heartImg}
        gap="gap-10 xl:gap-14"
      />
      <SplitTextSection
        theme="light"
        title="Supporting hungry kids"
        body="Annually, our team members get involved with employee volunteer programs like EatUp Australia, making more than 1400 sandwiches for kids that go to school without lunch, as part of our commitment to supporting low-income communities."
        image={"/images/about-us/sustainability/EatUp-Australia.png"}
        textRight
        gap="gap-10 xl:gap-14"
      />
      <SplitTextSection
        theme="dark-purpleblue"
        title="Our Lifeblood team "
        titleColour="text-green-light"
        body={
          <>
            <p>
              Our Lifeblood team have saved more than a staggering <span className="text-green-light">900 lives* </span> through the donation of blood, plasma
              and platelets.
            </p>
            <p>* Statistics obtained from the Australian Lifeblood Service and the Health Direct, an Australian Government initiative. </p>
          </>
        }
        image={"/images/about-us/sustainability/Australian-Lifeblood-Donor.png"}
        iconImage={heartImg}
        gap="gap-10 xl:gap-14"
      />
      <SplitTextSection
        theme="light"
        title={
          <h3 className="mb-4 leading-[1.2]">
            Volunteering in <span className="md:block"> the community</span>
          </h3>
        }
        body="In 2024 we kicked off our employee volunteering program, where we partner with core non-for-profit organisations to support team members who wish to donate their time to important causes. Our people dedicate their skills, experience and energy to initiatives that align with our values, creating meaningful impact beyond financial contributions."
        image={"/images/about-us/sustainability/GoKindly_Volunteer.png"}
        textRight
        gap="gap-10 xl:gap-14"
      />
      <SplitTextSection
        theme="dark-purpleblue"
        title={
          <h3 className="mb-4 leading-[1.2]">
            Supporting our <span className="md:block">little heroes</span>
          </h3>
        }
        titleColour="text-green-light"
        body={`Since our founding, we've supported the Royal Children’s Hospital Good Friday Appeal every year through donations and activities to raise awareness for their incredible work.`}
        image={"/images/about-us/sustainability/Good Friday Appeal.png"}
        imgSize="max-w-[300px] mx-auto sm:mx-0"
        iconImage={heartImg}
        gap="gap-10 xl:gap-14"
        textRight
      />
      <SimpleTextSection
        theme="light"
        title={
          <>
            <Image
              src="/images/Community Logos/BullyZero.png"
              alt="bully-zero"
              width={400}
              height={400}
              className="mb-6 mx-auto w-full max-w-80 lg:max-w-[26rem]"
            />
            <h2>Creating a world free from bullying</h2>
          </>
        }
        titleColour="text-white"
        body={
          <p className="mt-3">
            <span>
              In 2013, our Founder and Chief Executive Officer co-foundered the Bully Zero Australia Foundation to create a world free from all forms of
              bullying.
            </span>
            <span className="md:block"> We remain a passionate supporter of Bully Zero today.</span>
          </p>
        }
        bodyWidth="max-w-3xl lg:max-w-4xl"
      />
      <FeatureGrid
        theme="dark-purpleblue"
        title={`Donating to causes that matter`}
        titleColour="text-green-light"
        body={[
          `Each year, we contribute to over 30 for-purpose organisations to help them advance their work, supporting local charities and facilitating charity donations Australia wide`,
          `Some of the organisations we’ve partnered with:`,
        ]}
        bodyClass="mb-8"
        sectionVariant="center"
        itemVariant="large-image-contain"
        items={[
          { imageBg: "hover:bg-white/5", image: "/images/Community Logos/Steptember3.png", alt: "Steptember" },
          { imageBg: "hover:bg-white/5", image: "/images/Community Logos/MOvember-white.png", alt: "MOvember" },
          { imageBg: "hover:bg-white/5", image: "/images/Community Logos/Australian_Red_Cross_White.png", alt: "Australian Red Cross" },
          { imageBg: "hover:bg-white/5", image: "/images/Community Logos/the-smith-family-logo-social-sharing-200.png", alt: "Smith Family" },
          { imageBg: "hover:bg-white/5", image: "/images/Community Logos/Cancer-Council-Victoria-Logo-310.png", alt: "Canser Council Victoria" },
          { imageBg: "hover:bg-white/5", image: "/images/Community Logos/eWastec-Logo-white.png", alt: "eWastec" },
        ]}
      />

      {/* <SplitTextSection
        theme="dark"
        title={
          <h2 className="mb-4 text-3xl md:text-4xl lg:text-5xl xl:text-6xl">
            100% <span className="text-orange">On-Shore</span>
          </h2>
        }
        body={
          <>
            <p className="max-w-[32rem] text-lg/6 xl:text-xl">
              All of our team members reside in Australia and we maintain a local first supplier procurement policy to ensure we provide maximum support for our
              local economy.
            </p>
          </>
        }
        image={australiaShapedImg}
        extraImageClass="md:translate-y-4"
        alignItemsClass="items-center"
        iconImage={heartImg}
        padding="px-8 pt-16 pb-10 sm:pt-20 md:py-24 ml:px-16 xl:pt-32 xl:pb-16"
        customTextWidth="md:w-1/2 xl:w-[45%]"
      /> */}
      {/* <SimpleTextSection
        bgClass={`bg-[url('/images/First-Nation-Background.png')] bg-no-repeat bg-center bg-cover bg-grey-65/15 border-t-4 border-b-4 border-white`}
        title={<h2 className="text-desk-resp-4_5xl mb-8 font-normal text-purpleblue xl:mb-12">Supporting our First Nations people</h2>}
        body={
          <>
            <p className="xl:text-2xl">
              We are creating meaningful careers in the tech sector for First Nations People combined with helping them to recount their 65,000 years of
              history.
            </p>
          </>
        }
        bodyWidth="w-full sm:w-3/4 lg:w-[40rem] xl:w-[40rem]"
        afterAction={
          <div className="leading-3 xl:mt-2">
            <TextLink href="/about-us/reconciliation" className="text-desk-resp-xl font-bold text-purpleblue xl:text-2xl" hoverVariant="background">
              Learn more about our commitment to First Nations People
              <ArrowLongRightIcon className="mb-0.5 ml-1 inline size-5 lg:size-6" />
            </TextLink>
          </div>
        }
        multiLine
      /> */}
      {/* <FeatureGrid
        theme="dark-revert-purpleblue"
        title={
          <h2 className={``}>
            <span>6% of our non-inventory supply chain is </span>
            <span className="lg:block">procured from Social Enterprises</span>
          </h2>
        }
        items={SOCIAL_ENTERPRISES}
        gridClass="sm:grid-cols-2 lg:grid-cols-3 justify-items-center gap-8"
        padding="sm"
        afterAction="And many many more!"
      /> */}
      {/* <FeatureGrid
        theme="light"
        title={<h2 className="font-normal">We help others</h2>}
        items={WE_HELP_OTHERS}
        itemVariant="large-image"
        maxWidth="lg:max-w-6xl"
        padding="md"
      /> */}
      {/* <CharitiesAnimateSection ref={charitiesRef} link={false} /> */}
      {/* <NetZeroTextSection link={false} /> */}
      {/* <EwasteSection theme="dark" titleColour="text-green" hasIconImage /> */}
      <SimpleTextSection
        bgClass={`bg-[url('/images/Aboriginal-Artwork---Background.png')] bg-no-repeat bg-center bg-cover ${getBorderClass}`}
        title={`Taking a stance on reconciliation`}
        titleColour="text-white"
        body={
          <p className="text-white">
            As a proud Aussie company, we strive to reflect Australia’s rich and diverse culture. A culture that has been here for over 65,000 years longer than
            we have.
          </p>
        }
        bodyWidth="max-w-2xl lg:max-w-3xl"
        afterAction={
          <TextLink
            href="/about-us/reconciliation"
            className="text-desk-resp-xl font-bold text-orange-light"
            hoverVariant="background"
            hoverColor="bg-orange"
            arrow
          >
            Learn about our approach to reconciliation
          </TextLink>
        }
      />
      <SimpleTextSection
        bgClass={`bg-[url('/images/Protecting-Our-Planet-Environmental-Sustainability.png')] bg-no-repeat bg-center bg-cover`}
        title={`Protecting our beautiful planet `}
        titleColour="text-green-light"
        body={
          <p className="text-white">
            We couldn’t be more passionate about protecting our environment, and we are proud to be a leading example in our sector of what is possible through
            our achievements to date.
          </p>
        }
        bodyWidth="max-w-2xl lg:max-w-3xl"
        afterAction={
          <TextLink
            href="/about-us/community-and-sustainability/environmental"
            className="text-desk-resp-xl font-bold text-green-light"
            hoverVariant="background"
            hoverColor="bg-green"
            arrow
          >
            Learn about our Environmental Sustainability
          </TextLink>
        }
        xPadding="px-8 md:px-12 lg:px-20"
        divClass="items-start justify-center text-left"
      />
      <CommunityTextSection customBorder={`${getBorderClass}`} />
    </>
  )
}
export default CommunitySustainabilityPageLayout
